import {
  useExchangeSpec,
  useFieldOptions,
  useImportPrefs,
  useIngredientCatalog,
  useSaveImportPrefs,
} from '@/api/hooks'
import type { ContextPart, ImportPrefs } from '@/api/types'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Spinner } from '@/components/ui/Feedback'
import { Textarea } from '@/components/ui/Field'
import { Note } from '@/components/ui/Note'
import { useToast } from '@/components/ui/toastContext'
import { downloadText } from '@/lib/download'
import { pluralise } from '@/lib/format'
import { useDebounced } from '@/lib/useDebounced'
import { Copy, Download } from 'lucide-react'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { CONTEXT_PARTS, buildContext, renderPart, type ContextSources } from './context'

const INSTRUCTIONS_MAX = 4000

const TITLES: Record<ContextPart, string> = {
  format: 'Import format',
  instructions: 'Your instructions',
  vocabulary: 'Your vocabulary',
  catalog: 'Ingredient catalog',
}

/**
 * Everything the LLM needs, shown as the list of parts it is made of. What is
 * ticked here is exactly what Copy and Download produce.
 */
export function LlmContextCard() {
  const prefs = useImportPrefs()
  return (
    <Card className="min-w-0">
      <CardHeader
        title="1. Give your LLM the context"
        subtitle="Copy this into the chat, then ask for a recipe: from a link, a pasted recipe, or your own idea."
      />
      {prefs.data ? (
        <ContextPackage initial={prefs.data} />
      ) : prefs.isError ? (
        <CardBody>
          <Note tone="critical">Could not load your import settings.</Note>
        </CardBody>
      ) : (
        <CardBody>
          <Spinner label="Loading" />
        </CardBody>
      )}
    </Card>
  )
}

function ContextPackage({ initial }: { initial: ImportPrefs }) {
  const toast = useToast()
  const spec = useExchangeSpec()
  const vocabulary = useFieldOptions()
  const catalog = useIngredientCatalog()
  const save = useSaveImportPrefs()

  const [instructions, setInstructions] = useState(initial.instructions)
  const [excluded, setExcluded] = useState<ContextPart[]>(initial.excluded_parts)
  const [open, setOpen] = useState<ContextPart | null>(null)

  // Saved on their own after a pause, like the remembered weights: there is no
  // Save button to forget. `saved` is what the server has, so an edit that is
  // typed and then undone does not cause a request.
  const saved = useRef(initial)
  const latest = useRef({ instructions, excluded })
  latest.current = { instructions, excluded }
  const debounced = useDebounced(instructions, 600)

  function persist(next: ImportPrefs) {
    const current = saved.current
    if (
      next.instructions.trim() === current.instructions &&
      next.excluded_parts.join() === current.excluded_parts.join()
    ) {
      return
    }
    saved.current = { instructions: next.instructions.trim(), excluded_parts: next.excluded_parts }
    save.mutate(next, {
      onError: () => {
        saved.current = current
        toast.error('Could not save your import settings.')
      },
    })
  }

  useEffect(() => {
    persist({ instructions: debounced, excluded_parts: latest.current.excluded })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced])

  // Leaving within the pause would otherwise drop the last edit.
  useEffect(
    () => () =>
      persist({
        instructions: latest.current.instructions,
        excluded_parts: latest.current.excluded,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  function toggle(part: ContextPart) {
    const next = excluded.includes(part)
      ? excluded.filter((item) => item !== part)
      : [...excluded, part].sort()
    setExcluded(next)
    persist({ instructions, excluded_parts: next })
  }

  const sources: ContextSources = {
    format: spec.data,
    instructions,
    vocabulary: vocabulary.data,
    catalog: catalog.data,
  }
  const hasContent = (part: ContextPart) => renderPart(part, sources) !== null
  const included = CONTEXT_PARTS.filter((part) => !excluded.includes(part) && hasContent(part))
  const loading = spec.isPending || vocabulary.isPending || catalog.isPending
  const ready = !loading && included.length > 0

  async function copy() {
    try {
      await navigator.clipboard.writeText(buildContext(sources, excluded))
      toast.success(`Copied ${included.map((part) => TITLES[part].toLowerCase()).join(', ')}.`)
    } catch {
      toast.error('Could not copy to the clipboard.')
    }
  }

  const summaries: Record<ContextPart, ReactNode> = {
    format: 'How to write the recipe JSON the app reads.',
    instructions: instructions.trim() ? (
      <span className="line-clamp-1 italic">“{instructions.trim()}”</span>
    ) : (
      'Targets, equipment, house rules you always want. Not set yet.'
    ),
    vocabulary: vocabulary.data
      ? `${pluralise(vocabulary.data.cuisines.length, 'cuisine')} · ` +
        `${pluralise(vocabulary.data.protein_bases.length, 'protein base')} · ` +
        `${pluralise(vocabulary.data.tags.length, 'tag')}, so new recipes use the same words.`
      : 'Loading…',
    catalog: catalog.data
      ? `${pluralise(catalog.data.ingredients.length, 'ingredient')} with macros, matched by name.`
      : 'Loading…',
  }

  return (
    <CardBody className="space-y-4">
      <ul className="divide-y divide-line rounded-[var(--radius-field)] border border-line">
        {CONTEXT_PARTS.map((part) => {
          const empty = !hasContent(part)
          const isOpen = open === part
          const editable = part === 'instructions'
          return (
            <li key={part} className="px-3 py-2.5">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id={`part-${part}`}
                  checked={!excluded.includes(part) && !empty}
                  disabled={empty && !loading}
                  onChange={() => toggle(part)}
                  className="mt-0.5 size-4 shrink-0 rounded"
                  title={empty && !loading ? 'Nothing to include yet' : undefined}
                />
                <label htmlFor={`part-${part}`} className="min-w-0 flex-1 cursor-pointer">
                  <span className="block text-sm font-medium text-ink">{TITLES[part]}</span>
                  <span className="mt-0.5 block text-micro text-pretty text-ink-faint">
                    {summaries[part]}
                  </span>
                </label>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-expanded={isOpen}
                  onClick={() => setOpen(isOpen ? null : part)}
                >
                  {isOpen ? 'Close' : editable ? (empty ? 'Add' : 'Edit') : 'View'}
                </Button>
              </div>

              {isOpen && editable ? (
                <div className="mt-2.5 pl-7">
                  <Textarea
                    aria-label="Standing instructions for the LLM"
                    value={instructions}
                    onChange={(event) => setInstructions(event.target.value)}
                    rows={6}
                    maxLength={INSTRUCTIONS_MAX}
                    autoFocus
                    placeholder={
                      'About 600 kcal and 40 g protein per serving.\n' +
                      'I have an 8 qt Instant Pot and a rice cooker.\n' +
                      '5 servings unless the recipe says otherwise.'
                    }
                  />
                  <p className="tabular mt-1 flex justify-between text-micro text-ink-faint">
                    <span>
                      {save.isPending
                        ? 'Saving…'
                        : instructions.trim() === saved.current.instructions
                          ? 'Saved to your account. Included when you copy.'
                          : 'Unsaved changes…'}
                    </span>
                    {instructions.length > INSTRUCTIONS_MAX * 0.8 ? (
                      <span>
                        {instructions.length} / {INSTRUCTIONS_MAX}
                      </span>
                    ) : null}
                  </p>
                </div>
              ) : null}

              {isOpen && !editable ? (
                <pre className="mt-2.5 ml-7 max-h-80 overflow-auto rounded-[var(--radius-field)] bg-surface-sunken p-3 font-mono text-micro whitespace-pre-wrap text-ink-soft">
                  {renderPart(part, sources) ?? 'Nothing here yet.'}
                </pre>
              ) : null}
            </li>
          )
        })}
      </ul>

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="primary" size="sm" disabled={!ready} onClick={() => void copy()}>
          <Copy size={14} aria-hidden />
          Copy for LLM
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={!ready}
          onClick={() => downloadText('meal-planner-llm-context.md', buildContext(sources, excluded), 'text/markdown')}
        >
          <Download size={14} aria-hidden />
          Download as file
        </Button>
        <span className="text-micro text-ink-faint">
          {loading ? 'Loading…' : `${included.length} of ${CONTEXT_PARTS.length} parts`}
        </span>
      </div>
      <p className="text-micro text-pretty text-ink-faint">
        Using a chat project? Add the format to the project once, then untick it here.
      </p>
    </CardBody>
  )
}
